import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, message, Space, Typography, Table } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { scheduleApi } from '../api/schedule';
import { ScheduleLocation } from '../api/model';

const { Title, Text } = Typography;

interface ScheduleLocationFormValues {
  name: string;
  address: string;
}

const ScheduleLocations: React.FC = () => {
  const [locations, setLocations] = useState<ScheduleLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<ScheduleLocation | null>(null);
  const [form] = Form.useForm<ScheduleLocationFormValues>();
  const [updateForm] = Form.useForm<ScheduleLocationFormValues>();

  // 获取日程地点列表
  const fetchScheduleLocations = async () => {
    setLoading(true);
    try {
      const response = await scheduleApi.listScheduleLocations();
      
      if (response.success) {
        setLocations(response.data);
      } else {
        message.error(response.message || '获取日程地点失败');
      }
    } catch (error) {
      console.error('获取日程地点失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 提交创建表单
  const handleCreateSubmit = async (values: ScheduleLocationFormValues) => {
    try {
      const response = await scheduleApi.createScheduleLocation(values);
      if (response.success) {
        message.success('日程地点创建成功');
        setCreateModalVisible(false);
        form.resetFields();
        // 重新获取日程地点列表
        fetchScheduleLocations();
      } else {
        message.error(response.message || '创建日程地点失败');
      }
    } catch (error) {
      console.error('创建日程地点失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交更新表单
  const handleUpdateSubmit = async (values: ScheduleLocationFormValues) => {
    if (!currentLocation) return;
    
    try {
      const response = await scheduleApi.updateScheduleLocation(currentLocation.id, values);
      if (response.success) {
        message.success('日程地点更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentLocation(null);
        // 重新获取日程地点列表
        fetchScheduleLocations();
      } else {
        message.error(response.message || '更新日程地点失败');
      }
    } catch (error) {
      console.error('更新日程地点失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 删除日程地点
  const handleDelete = async (locationId: string) => {
    try {
      const response = await scheduleApi.deleteScheduleLocation(locationId);
      if (response.success) {
        message.success('日程地点删除成功');
        // 重新获取日程地点列表
        fetchScheduleLocations();
      } else {
        message.error(response.message || '删除日程地点失败');
      }
    } catch (error) {
      console.error('删除日程地点失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 初始化获取数据
  useEffect(() => {
    fetchScheduleLocations();
  }, []);

  // 打开更新模态框
  const openUpdateModal = (location: ScheduleLocation) => {
    setCurrentLocation(location);
    setUpdateModalVisible(true);
    // 设置表单初始值
    updateForm.setFieldsValue({
      name: location.name,
      address: location.address,
    });
  };

  const columns = [
    {
      title: '地点名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, location: ScheduleLocation) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => openUpdateModal(location)}
          />
          <Button 
            type="link" 
            icon={<DeleteOutlined />} 
            danger
            onClick={() => handleDelete(location.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div>
        <h2>日程地点管理</h2>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          新建日程地点
        </Button>
      </div>
      
      <Table
        loading={loading}
        dataSource={locations}
        columns={columns}
        rowKey="id"
        pagination={false}
      />

      {/* 创建日程地点模态框 */}
      <Modal
        title="新建日程地点"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          <Form.Item
            name="name"
            label="地点名称"
            rules={[{ required: true, message: '请输入地点名称' }]}
          >
            <Input placeholder="请输入地点名称" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="address"
            label="地址"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input placeholder="请输入地址" autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 更新日程地点模态框 */}
      <Modal
        title="编辑日程地点"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
          setCurrentLocation(null);
        }}
        onOk={() => updateForm.submit()}
        okText="更新"
        cancelText="取消"
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdateSubmit}>
          <Form.Item
            name="name"
            label="地点名称"
            rules={[{ required: true, message: '请输入地点名称' }]}
          >
            <Input placeholder="请输入地点名称" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="address"
            label="地址"
            rules={[{ required: true, message: '请输入地址' }]}
          >
            <Input placeholder="请输入地址" autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduleLocations;